import { useEffect, useState } from "react";
import { makeStyles, tokens } from "@fluentui/react-components";
import { useNavigate } from "react-router-dom";
import { AppDialog, DialogActionsBar, DialogChoiceGroup, DialogField } from "@/components/app-dialog";
import { DateField } from "@/components/date-field";
import { FuiInput } from "@/components/fui-control";
import { canWorkspace, useWorkspaceRole } from "@/features/access/workspace-access";
import { todayISO } from "@/engine/date";
import { useCatalogStore } from "@/store/catalog-store";
import { useProjectStore } from "@/store/project-store";
import { FuiDropdown } from "@/components/fui-control";

const useStyles = makeStyles({
  form: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    minWidth: 0,
  },
});

const EMPTY_NAME = "새 플랜";
const SAMPLE_NAME = "ERP 구축 샘플";

function defaultNameForTemplate(template: "empty" | "sample" | "saved", savedName?: string) {
  if (template === "sample") return SAMPLE_NAME;
  if (template === "saved") return savedName?.trim() || EMPTY_NAME;
  return EMPTY_NAME;
}

/** 옛 `/projects/new` 주소. 대화상자를 연 뒤 플랜 목록으로 되돌린다. */
export function CreatePlanRoute() {
  const navigate = useNavigate();
  const allowCreate = canWorkspace(useWorkspaceRole(), "plan.create");
  const guest = useProjectStore((s) => s.previewGuest);
  const guestPlanId = useProjectStore((s) => s.previewGuestPlanId);

  useEffect(() => {
    if (guest || !allowCreate) {
      navigate(guest && guestPlanId ? `/projects/${guestPlanId}/schedule` : "/", { replace: true });
      return;
    }
    useProjectStore.getState().openDialog("create-plan");
    navigate("/", { replace: true });
  }, [allowCreate, guest, guestPlanId, navigate]);

  return null;
}

export function CreatePlanDialog() {
  const styles = useStyles();
  const navigate = useNavigate();
  const open = useProjectStore((s) => s.dialog === "create-plan");
  const allowCreate = canWorkspace(useWorkspaceRole(), "plan.create");
  const createProject = useCatalogStore((s) => s.createProject);
  const templates = useCatalogStore((s) => s.workspaces[s.activeWorkspaceId]?.projectTemplates ?? []);
  const openProject = useProjectStore((s) => s.openProject);
  const [name, setName] = useState(SAMPLE_NAME);
  const [start, setStart] = useState(todayISO);
  const [template, setTemplate] = useState<"empty" | "sample" | "saved">("sample");
  const [templateId, setTemplateId] = useState("");

  useEffect(() => {
    if (!open) return;
    if (!allowCreate) {
      useProjectStore.getState().openDialog(null);
      return;
    }
    setName(SAMPLE_NAME);
    setStart(todayISO());
    setTemplate("sample");
    setTemplateId("");
  }, [open, allowCreate]);

  const close = () => useProjectStore.getState().openDialog(null);

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed || !allowCreate) return;
    const savedId = template === "saved" ? templateId : "";
    if (template === "saved" && !savedId) return;
    const id = createProject({
      name: trimmed,
      start,
      template: template === "sample" ? "sample" : "empty",
      ...(savedId ? { projectTemplateId: savedId } : {}),
    });
    openProject(id);
    close();
    navigate(`/projects/${id}/schedule`);
  };

  return (
    <AppDialog
      open={open && allowCreate}
      onClose={close}
      title="새 플랜"
      width="sm"
        footer={<DialogActionsBar onCancel={close} onConfirm={submit} confirmLabel="만들기" disabled={!name.trim() || (template === "saved" && !templateId)} />}
    >
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <DialogField label="플랜 이름" required>
          <FuiInput density="dialog" value={name} autoFocus aria-label="플랜 이름" onChange={(_, d) => setName(d.value)} />
        </DialogField>
        <DialogField label="시작일" required>
          <DateField size="sm" value={start} onChange={(v) => v && setStart(v)} aria-label="시작일" />
        </DialogField>
        <DialogChoiceGroup
          label="템플릿"
          value={template}
          onChange={(next) => {
            setTemplate(next);
            const savedName = templates.find((item) => item.id === templateId)?.name;
            setName((current) => (current === EMPTY_NAME || current === SAMPLE_NAME || templates.some((item) => item.name === current) ? defaultNameForTemplate(next, savedName) : current));
          }}
          options={[
            { value: "sample", label: "ERP 구축 샘플", hint: "약 40개 작업으로 시연" },
            { value: "empty", label: "빈 플랜", hint: "작업 하나부터" },
            ...(templates.length ? [{ value: "saved" as const, label: "저장한 템플릿", hint: "WBS와 의존성" }] : []),
          ]}
        />
        {template === "saved" ? (
          <DialogField label="저장한 템플릿" required>
            <FuiDropdown
              density="dialog"
              aria-label="저장한 템플릿"
              value={templateId}
              placeholder="템플릿"
              options={templates.map((item) => ({ value: item.id, label: item.name }))}
              onValueChange={(value) => {
                setTemplateId(value);
                const savedName = templates.find((item) => item.id === value)?.name;
                setName((current) => (current === EMPTY_NAME || current === SAMPLE_NAME || templates.some((item) => item.name === current) ? savedName || current : current));
              }}
            />
          </DialogField>
        ) : null}
        <button type="submit" hidden />
      </form>
    </AppDialog>
  );
}
